import { useMemo, useState } from 'react'
import { AlertTriangle, Info, Landmark, Pencil, Play, Plus, Search, Trash2 } from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FilterBar, FormDialog, PageHeader,
  StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useBankAcknowledgements, useBankChannelMutations, useBankChannels } from '../api'
import type { BankChannelView, PollResult } from '../types'

interface Draft {
  id?: string
  name: string
  sftpHost: string
  sftpPort: string
  sftpUsername: string
  sftpPassword: string
  sftpHostKey: string
  outboundPath: string
  ackPath: string
  ackFilePattern: string
  ackDelimiter: string
  ackReferenceColumn: string
  ackStatusColumn: string
  ackPaidValues: string
  ackRejectedValues: string
  active: boolean
  passwordStored: boolean
}

const EMPTY: Draft = {
  name: '',
  sftpHost: '',
  sftpPort: '22',
  sftpUsername: '',
  sftpPassword: '',
  sftpHostKey: '',
  outboundPath: '',
  ackPath: '',
  ackFilePattern: 'ACK_*.csv',
  ackDelimiter: ',',
  // No defaults for these four. Every bank differs and a guess reports salaries as paid.
  ackReferenceColumn: '',
  ackStatusColumn: '',
  ackPaidValues: '',
  ackRejectedValues: '',
  active: true,
  passwordStored: false,
}

function toDraft(channel: BankChannelView): Draft {
  return {
    id: channel.id,
    name: channel.name,
    sftpHost: channel.sftpHost,
    sftpPort: channel.sftpPort == null ? '22' : String(channel.sftpPort),
    sftpUsername: channel.sftpUsername,
    sftpPassword: '',
    sftpHostKey: channel.sftpHostKey,
    outboundPath: channel.outboundPath,
    ackPath: channel.ackPath,
    ackFilePattern: channel.ackFilePattern,
    ackDelimiter: channel.ackDelimiter,
    ackReferenceColumn: String(channel.ackReferenceColumn),
    ackStatusColumn: String(channel.ackStatusColumn),
    ackPaidValues: channel.ackPaidValues,
    ackRejectedValues: channel.ackRejectedValues,
    active: channel.active,
    passwordStored: channel.passwordStored,
  }
}

/**
 * The bank connection, and what the bank said back.
 *
 * <p>The acknowledgement format is typed in rather than detected. No two banks agree on which
 * column holds the reference or which word means paid — "PAID", "P", "SUCCESS", "00" are all
 * real — and a wrong guess here tells a company that salaries went out when they bounced. A
 * status word in neither list produces UNRECOGNISED, which changes nothing and appears on this
 * screen for somebody to decide.
 */
export function BankChannelsPage() {
  useBreadcrumb()
  const channels = useBankChannels()
  const { create, update, remove, pollNow } = useBankChannelMutations()

  const [channelFilter, setChannelFilter] = useState<string>('')
  const [outcome, setOutcome] = useState<string>('')
  const [search, setSearch] = useState('')
  const acks = useBankAcknowledgements(channelFilter || undefined)

  const [draft, setDraft] = useState<Draft | null>(null)
  const [toRemove, setToRemove] = useState<BankChannelView | null>(null)
  const [lastPoll, setLastPoll] = useState<PollResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const visibleAcks = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (acks.data ?? [])
      .filter((ack) => outcome === '' || ack.outcome === outcome)
      .filter((ack) => term === ''
        || (ack.reference ?? '').toLowerCase().includes(term)
        || ack.fileName.toLowerCase().includes(term))
  }, [acks.data, outcome, search])

  const unrecognised = (acks.data ?? []).filter((ack) => ack.outcome === 'UNRECOGNISED').length

  async function onSave() {
    if (!draft) return
    setError(null)
    const body = {
      name: draft.name,
      bankId: null,
      sftpHost: draft.sftpHost,
      sftpPort: draft.sftpPort ? Number(draft.sftpPort) : null,
      sftpUsername: draft.sftpUsername,
      // Sent only when typed: the stored one never reaches this screen, so echoing an empty
      // string back would clear it on every save.
      ...(draft.sftpPassword ? { sftpPassword: draft.sftpPassword } : {}),
      sftpHostKey: draft.sftpHostKey,
      outboundPath: draft.outboundPath,
      ackPath: draft.ackPath,
      ackFilePattern: draft.ackFilePattern,
      ackDelimiter: draft.ackDelimiter,
      ackReferenceColumn: Number(draft.ackReferenceColumn),
      ackStatusColumn: Number(draft.ackStatusColumn),
      ackPaidValues: draft.ackPaidValues,
      ackRejectedValues: draft.ackRejectedValues,
      active: draft.active,
    }
    try {
      if (draft.id) await update.mutateAsync({ id: draft.id, body })
      else await create.mutateAsync(body)
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That channel could not be saved')
    }
  }

  async function onPoll(id: string) {
    setLastPoll(null)
    setError(null)
    try {
      setLastPoll(await pollNow.mutateAsync(id))
      await acks.refetch()
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That channel could not be polled')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bank channels"
        subtitle="Where the salary file goes, and what the bank answered."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            New channel
          </Button>
        }
      />

      {/* The same failure must not appear twice. While a dialog is open the reason belongs
          inside it, next to the field that caused it. */}
      {error && draft == null && (
        <Card role="alert" className="border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </Card>
      )}

      {lastPoll && (
        <Card
          role="status"
          className={`p-4 text-sm ${lastPoll.ok
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
            : 'border-red-300 bg-red-50 text-red-900'}`}
        >
          <p className="font-medium">{lastPoll.channelName}</p>
          <p>{lastPoll.message}</p>
        </Card>
      )}

      <Card>
        {channels.isPending && <Skeleton className="m-4 h-24" />}
        {channels.isError && (
          <ErrorState error={channels.error} onRetry={() => void channels.refetch()} />
        )}
        {channels.isSuccess && channels.data.length === 0 && (
          <EmptyState
            title="No bank channel"
            description="Add the bank's SFTP details and the format of its acknowledgement file, and salary files can be delivered and their answers read without anybody logging in."
          />
        )}
        {channels.isSuccess && channels.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 font-medium">Channel</th>
                  <th className="p-3 font-medium">Server</th>
                  <th className="p-3 font-medium">Acknowledgements</th>
                  <th className="p-3 font-medium">Last poll</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {channels.data.map((channel) => (
                  <tr key={channel.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <span className="flex items-center gap-2">
                        <Landmark className="size-4 text-slate-500" aria-hidden="true" />
                        {channel.name}
                      </span>
                      {!channel.active && <span className="text-xs text-slate-500">Switched off</span>}
                    </td>
                    <td className="p-3 text-slate-600">
                      <span className="font-mono text-xs">
                        {channel.sftpUsername}@{channel.sftpHost}:{channel.sftpPort ?? 22}
                      </span>
                      <span className="block text-xs text-slate-500">{channel.outboundPath}</span>
                    </td>
                    <td className="p-3 text-slate-600">
                      <span className="font-mono text-xs">
                        {channel.ackPath}/{channel.ackFilePattern}
                      </span>
                      <span className="block text-xs text-slate-500">
                        paid: {channel.ackPaidValues} · rejected: {channel.ackRejectedValues}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">
                      {channel.lastPolledAt
                        ? <DateValue value={channel.lastPolledAt} />
                        : 'Never polled'}
                      {channel.lastPollResult && (
                        <span className="block max-w-xs text-xs text-slate-500">
                          {channel.lastPollResult}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void onPoll(channel.id)}
                      >
                        <Play className="size-4" aria-hidden="true" />
                        Poll now
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { setError(null); setDraft(toDraft(channel)) }}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { setError(null); setToRemove(channel) }}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* --------------------------------------------- what the bank said */}
      <div className="space-y-3">
        <h2 className="font-medium">What the banks have said</h2>

        {unrecognised > 0 && (
          <Card role="alert" className="border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              {unrecognised} acknowledgement line{unrecognised === 1 ? '' : 's'} carried a status
              word this product was not told the meaning of. Nothing was changed for them. Add
              the word to the channel's paid or rejected list once you know which it is.
            </p>
          </Card>
        )}

        <FilterBar
          search={
            <div className="relative">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
                aria-hidden="true"
              />
              <Input
                aria-label="Search acknowledgements"
                className="ps-9"
                placeholder="Search by reference or file"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          }
        >
          <Select
            aria-label="Channel"
            value={channelFilter}
            onChange={(event) => setChannelFilter(event.target.value)}
          >
            <option value="">Every channel</option>
            {channels.data?.map((channel) => (
              <option key={channel.id} value={channel.id}>{channel.name}</option>
            ))}
          </Select>
          <Select
            aria-label="Outcome"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
          >
            <option value="">Every outcome</option>
            <option value="PAID">Paid</option>
            <option value="REJECTED">Rejected</option>
            <option value="UNRECOGNISED">Unrecognised</option>
          </Select>
        </FilterBar>

        <Card>
          {acks.isPending && <Skeleton className="m-4 h-24" />}
          {acks.isError && <ErrorState error={acks.error} onRetry={() => void acks.refetch()} />}
          {acks.isSuccess && visibleAcks.length === 0 && (
            <EmptyState
              title="Nothing from the bank yet"
              description="Acknowledgement files are read on a timer, and every line read is kept here — including the ones this product would not interpret."
            />
          )}
          {acks.isSuccess && visibleAcks.length > 0 && (
            <div className="overflow-x-auto overscroll-x-contain">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-start text-slate-600">
                  <tr>
                    <th className="p-3 font-medium">Reference</th>
                    <th className="p-3 font-medium">Bank said</th>
                    <th className="p-3 font-medium">Read as</th>
                    <th className="p-3 font-medium">File</th>
                    <th className="p-3 font-medium">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAcks.map((ack) => (
                    <tr key={ack.id} className="border-b border-slate-100">
                      <td className="p-3 font-mono text-xs">{ack.reference ?? '—'}</td>
                      <td className="p-3 text-slate-600">{ack.bankStatus ?? '—'}</td>
                      <td className="p-3">
                        <StatusBadge status={ack.outcome} />
                        {ack.detail && (
                          <span className="block max-w-md text-xs text-slate-600">{ack.detail}</span>
                        )}
                      </td>
                      <td className="p-3 font-mono text-xs text-slate-500">{ack.fileName}</td>
                      <td className="p-3 text-slate-600"><DateValue value={ack.receivedAt} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title={draft?.id ? 'Edit channel' : 'New bank channel'}
        description="The host key and the status words are required — there is nothing safe to default them to."
        submitLabel="Save"
        loading={create.isPending || update.isPending}
        // Inside the dialog, not behind it: a modal that fails and shows the reason on the
        // page underneath is a modal that appears to have done nothing.
        error={draft ? error : null}
        onSubmit={() => void onSave()}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="channel-name">Name</Label>
            <Input
              id="channel-name"
              value={draft?.name ?? ''}
              placeholder="Emirates NBD host-to-host"
              onChange={(event) => setDraft((c) => c ? { ...c, name: event.target.value } : c)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Label htmlFor="channel-host">SFTP host</Label>
              <Input
                id="channel-host"
                value={draft?.sftpHost ?? ''}
                onChange={(event) => setDraft((c) => c ? { ...c, sftpHost: event.target.value } : c)}
              />
            </div>
            <div>
              <Label htmlFor="channel-port">Port</Label>
              <Input
                id="channel-port"
                value={draft?.sftpPort ?? ''}
                onChange={(event) => setDraft((c) => c ? { ...c, sftpPort: event.target.value } : c)}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="channel-user">Username</Label>
              <Input
                id="channel-user"
                value={draft?.sftpUsername ?? ''}
                onChange={(event) => setDraft((c) => c ? { ...c, sftpUsername: event.target.value } : c)}
              />
            </div>
            <div>
              <Label htmlFor="channel-password">Password</Label>
              <Input
                id="channel-password"
                type="password"
                placeholder={draft?.passwordStored ? 'Stored — leave blank to keep it' : ''}
                value={draft?.sftpPassword ?? ''}
                onChange={(event) => setDraft((c) => c ? { ...c, sftpPassword: event.target.value } : c)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="channel-hostkey">Pinned host key</Label>
            <Input
              id="channel-hostkey"
              value={draft?.sftpHostKey ?? ''}
              placeholder="SHA256:…"
              onChange={(event) => setDraft((c) => c ? { ...c, sftpHostKey: event.target.value } : c)}
            />
            <p className="mt-1 text-xs text-slate-500">
              Required. Read it with{' '}
              <span className="font-mono">ssh-keyscan -t rsa,ed25519 host | ssh-keygen -lf -</span>.
              Without it the first connection cannot be told apart from an interception, and this
              connection carries every salary in the company.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="channel-out">Outbound folder</Label>
              <Input
                id="channel-out"
                value={draft?.outboundPath ?? ''}
                placeholder="/upload"
                onChange={(event) => setDraft((c) => c ? { ...c, outboundPath: event.target.value } : c)}
              />
            </div>
            <div>
              <Label htmlFor="channel-ack">Acknowledgement folder</Label>
              <Input
                id="channel-ack"
                value={draft?.ackPath ?? ''}
                placeholder="/download"
                onChange={(event) => setDraft((c) => c ? { ...c, ackPath: event.target.value } : c)}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="channel-pattern">File pattern</Label>
              <Input
                id="channel-pattern"
                value={draft?.ackFilePattern ?? ''}
                placeholder="ACK_*.csv"
                onChange={(event) => setDraft((c) => c ? { ...c, ackFilePattern: event.target.value } : c)}
              />
            </div>
            <div>
              <Label htmlFor="channel-delimiter">Delimiter</Label>
              <Input
                id="channel-delimiter"
                value={draft?.ackDelimiter ?? ''}
                onChange={(event) => setDraft((c) => c ? { ...c, ackDelimiter: event.target.value } : c)}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="channel-refcol">Reference column</Label>
              <Input
                id="channel-refcol"
                inputMode="numeric"
                value={draft?.ackReferenceColumn ?? ''}
                onChange={(event) => setDraft((c) => c ? { ...c, ackReferenceColumn: event.target.value } : c)}
              />
            </div>
            <div>
              <Label htmlFor="channel-statuscol">Status column</Label>
              <Input
                id="channel-statuscol"
                inputMode="numeric"
                value={draft?.ackStatusColumn ?? ''}
                onChange={(event) => setDraft((c) => c ? { ...c, ackStatusColumn: event.target.value } : c)}
              />
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Column numbers start at 1, as they do in a spreadsheet.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="channel-paid">Words that mean paid</Label>
              <Input
                id="channel-paid"
                value={draft?.ackPaidValues ?? ''}
                placeholder="PAID,00,SUCCESS"
                onChange={(event) => setDraft((c) => c ? { ...c, ackPaidValues: event.target.value } : c)}
              />
            </div>
            <div>
              <Label htmlFor="channel-rejected">Words that mean rejected</Label>
              <Input
                id="channel-rejected"
                value={draft?.ackRejectedValues ?? ''}
                placeholder="REJECTED,RJ,FAIL"
                onChange={(event) => setDraft((c) => c ? { ...c, ackRejectedValues: event.target.value } : c)}
              />
            </div>
          </div>
          <p className="flex items-start gap-2 text-xs text-slate-500">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Comma-separated, and required. There is no standard set of words, so there is nothing
            to default to. Anything the bank sends that is in neither list is recorded as
            unrecognised and changes nobody's payment status.
          </p>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft?.active ?? true}
              onChange={(event) => setDraft((c) => c ? { ...c, active: event.target.checked } : c)}
            />
            Poll this channel for acknowledgements
          </label>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={toRemove != null}
        onOpenChange={(open) => { if (!open) setToRemove(null) }}
        title="Remove this channel?"
        description={`${toRemove?.name ?? 'That channel'} will stop being polled. Acknowledgements already received are kept.`}
        confirmLabel="Remove"
        destructive
        onConfirm={() => void (async () => {
          if (!toRemove) return
          setError(null)
          try {
            await remove.mutateAsync(toRemove.id)
            setToRemove(null)
          } catch (caught) {
            setError(toApiProblem(caught)?.detail ?? 'That channel could not be removed')
          }
        })()}
      />
    </div>
  )
}
